import { c as _c } from "react/compiler-runtime";
import { type ComponentType, createContext, useContext, useState } from "react";
export type ViewMode = "editing" | "submitted";
export function createContextState<T, P extends object = object>(defaults: T, extraProviderProps?: (props: P) => P) {
  type ContextType = {
    mode: ViewMode;
    submittedData: T;
    handleSuccess: (data: T) => void;
    handleEdit: () => void;
  } & P;
  const Context = createContext<ContextType | undefined>(undefined);
  function useContextState() {
    const context = useContext(Context);
    if (!context) {
      throw new Error("useContextState must be used within its Provider");
    }
    return context;
  }
  function ViewControl(t0) {
    const $ = _c(16);
    const {
      EditView,
      SubmitView,
      ...props
    } = t0;
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        mode: "editing",
        submittedData: defaults
      };
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    const [state, setState] = useState(t1);
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = data => {
        setState({
          mode: "submitted",
          submittedData: data
        });
      };
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    const handleSuccess = t2;
    let t3;
    if ($[2] !== state.submittedData) {
      t3 = () => {
        setState({
          mode: "editing",
          submittedData: state.submittedData
        });
      };
      $[2] = state.submittedData;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    const handleEdit = t3;
    const CurrentView = state.mode === "editing" ? EditView : SubmitView;
    const t4 = Context;
    const t5 = extraProviderProps ? extraProviderProps(props as P) : {};
    let t6;
    if ($[4] !== handleEdit || $[5] !== handleSuccess || $[6] !== state.mode || $[7] !== state.submittedData || $[8] !== t5) {
      t6 = {
        mode: state.mode,
        submittedData: state.submittedData,
        handleSuccess,
        handleEdit,
        ...t5
      };
      $[4] = handleEdit;
      $[5] = handleSuccess;
      $[6] = state.mode;
      $[7] = state.submittedData;
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    const t7 = t6 as ContextType;
    let t8;
    if ($[10] !== CurrentView) {
      t8 = <CurrentView />;
      $[10] = CurrentView;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    let t9;
    if ($[12] !== t4.Provider || $[13] !== t7 || $[14] !== t8) {
      t9 = <t4.Provider value={t7}>{t8}</t4.Provider>;
      $[12] = t4.Provider;
      $[13] = t7;
      $[14] = t8;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    return t9;
  }
  return {
    ViewControl,
    useContextState
  };
}